/**
 * Create or edit ONE DRAFT of an email template.
 *
 * ⚠️ PUBLISHING IS A VISIBILITY CHANGE, NOT A SEND. The alert in this dialog is
 * not decoration: "publish" is the most dangerous word on the screen, and an
 * operator who reads it as "send to 1,800 guests" will never press it — or,
 * worse, will press it believing they already have. The server agrees in the
 * payload (`sends_email: false`); this says it before the click.
 *
 * ONLY DRAFTS ARE EDITABLE. A published version is history — a coordinator may
 * already have copied it into their book — so the server refuses a PATCH with
 * 409 EDIT_PUBLISHED and the page starts a new version instead. This dialog is
 * therefore always editing a draft, never a live row.
 *
 * THE CONTRACT COMES FROM THE SERVER. Audiences, kinds, merge fields and the
 * subject/name/body limits all arrive in the GET payload and are passed in
 * here. Nothing in this file hardcodes them: the SQL's CHECK constraints, the
 * router's constants and these controls are one wire, and a copy of that list
 * living in a component is exactly how the two halves drift apart.
 */

import React from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  TemplateAudience, TemplateContract, TemplateKind, TemplateVersion,
} from '../weddingApi';

/** The five audiences, spelled the way an operator says them. */
export const AUDIENCE_LABEL: Record<string, string> = {
  guests: 'Guests',
  quiet_guests: 'Quiet guests',
  coordinators: 'Coordinators',
  couple: 'Couple',
  guests_platinum: 'Guests · Platinum',
};

export const KIND_LABEL: Record<string, string> = {
  invitation: 'Invitation',
  reminder: 'Reminder',
  thank_you: 'Thank you',
};

export const PUBLISHED_TOAST = 'Published — now selectable in every coordinator dashboard';

interface Props {
  open: boolean;
  /** NULL creates a new family at draft v1; a draft row edits that draft. */
  draft: TemplateVersion | null;
  contract: TemplateContract;
  onClose: () => void;
  /** Saved without publishing. */
  onSaved: (message: string) => void;
  /** Published. The page owns the toast copy. */
  onPublished: (liveCount: number) => void;
  onError: (message: string) => void;
}

const TemplateDialog: React.FC<Props> = ({
  open, draft, contract, onClose, onSaved, onPublished, onError,
}) => {
  const isEdit = draft !== null;

  const [name, setName] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [audience, setAudience] = React.useState<TemplateAudience>('guests');
  const [kind, setKind] = React.useState<TemplateKind>('invitation');
  const [saving, setSaving] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(draft ? draft.name : '');
    setSubject(draft ? draft.subject : '');
    setBody(draft ? draft.body : '');
    setAudience(draft ? draft.audience : (contract.audiences[0] || 'guests'));
    setKind(draft ? draft.kind : (contract.kinds[0] || 'invitation'));
    setSaving(false);
    setConfirming(false);
  }, [open, draft?.id]);

  /**
   * Inserts a merge field WHERE THE CURSOR IS, not at the end.
   *
   * A template body is written around its merge fields ("Hi {{guest_first_name}},
   * …"), so appending them to the bottom of the textarea would mean cutting and
   * pasting every single time. The selection is restored after the insert so
   * typing can carry straight on.
   */
  const insertMergeField = (field: string) => {
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + field);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? start;
    const next = body.slice(0, start) + field + body.slice(end);
    setBody(next);
    // The value lands on the next render, so move the caret after it.
    window.requestAnimationFrame(() => {
      el.focus();
      const caret = start + field.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const trimmedName = name.trim();
  const trimmedSubject = subject.trim();
  const nameTooLong = trimmedName.length > contract.name_max;
  const subjectTooLong = trimmedSubject.length > contract.subject_max;
  const bodyTooLong = body.length > contract.body_max;
  const canSave =
    trimmedName.length > 0 && !nameTooLong
    && trimmedSubject.length > 0 && !subjectTooLong
    && body.trim().length > 0 && !bodyTooLong
    && !saving;

  /** Writes the draft and answers with its id, whether created or patched. */
  const persist = async (): Promise<string> => {
    if (isEdit && draft) {
      await weddingApi.updateTemplate(draft.id, {
        name: trimmedName, subject: trimmedSubject, body, audience, kind,
      });
      return draft.id;
    }
    const created = await weddingApi.createTemplate({
      name: trimmedName, subject: trimmedSubject, body, audience, kind,
    });
    return created.template.id;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await persist();
      onSaved(`${trimmedName} saved as a draft. Coordinators cannot see it yet.`);
    } catch (e) {
      onError((e as WeddingApiError).message || 'Could not save the template');
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const id = await persist();
      const result = await weddingApi.publishTemplate(id);
      onPublished(result.live_count);
    } catch (e) {
      const err = e as WeddingApiError;
      onError(err.message || 'Could not publish the template');
      setSaving(false);
      setConfirming(false);
    }
  };

  const subjectHelper = (() => {
    if (subjectTooLong) return `${trimmedSubject.length} / ${contract.subject_max} — too long to save.`;
    if (trimmedSubject.length > contract.subject_soft_max) {
      return `${trimmedSubject.length} / ${contract.subject_max} — over ${contract.subject_soft_max} characters, most inboxes will cut it off.`;
    }
    return `${trimmedSubject.length} / ${contract.subject_max} characters.`;
  })();

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${draft?.name}` : 'New email template'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? `Draft version ${draft?.version}. Editing a draft never changes what a coordinator already copied.`
              : 'A new template starts as a draft at version 1. Publishing makes it selectable in every coordinator dashboard.'}
          </Typography>

          <TextField
            label="Template name"
            placeholder="e.g. Photo nudge — 10 days"
            value={name}
            size="small"
            fullWidth
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            error={nameTooLong}
            helperText={`${trimmedName.length} / ${contract.name_max} characters.`}
            inputProps={{ maxLength: contract.name_max }}
          />

          <TextField
            label="Subject line"
            placeholder="A picture is worth a chapter…"
            value={subject}
            size="small"
            fullWidth
            disabled={saving}
            onChange={(e) => setSubject(e.target.value)}
            error={subjectTooLong}
            helperText={subjectHelper}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Audience"
              value={audience}
              size="small"
              fullWidth
              disabled={saving}
              onChange={(e) => setAudience(e.target.value as TemplateAudience)}
            >
              {contract.audiences.map((a) => (
                <MenuItem key={a} value={a}>{AUDIENCE_LABEL[a] || a}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Kind"
              value={kind}
              size="small"
              fullWidth
              disabled={saving}
              onChange={(e) => setKind(e.target.value as TemplateKind)}
              helperText="Groups the template in the coordinator's list."
            >
              {contract.kinds.map((k) => (
                <MenuItem key={k} value={k}>{KIND_LABEL[k] || k}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box>
            <TextField
              label="Body"
              placeholder="Hi {{guest_first_name}}, {{couple}} would love…"
              value={body}
              size="small"
              fullWidth
              multiline
              minRows={6}
              disabled={saving}
              onChange={(e) => setBody(e.target.value)}
              error={bodyTooLong}
              helperText={`${body.length} / ${contract.body_max} characters.`}
              inputRef={bodyRef}
            />
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Insert:
              </Typography>
              {contract.merge_fields.map((field) => (
                <Chip
                  key={field}
                  size="small"
                  variant="outlined"
                  label={field}
                  disabled={saving}
                  onClick={() => insertMergeField(field)}
                />
              ))}
            </Stack>
          </Box>

          {/*
            Stated on the screen where the decision is made, not in a runbook.
          */}
          <Alert severity="info" variant="outlined">
            <AlertTitle>Publishing does not send anything</AlertTitle>
            Publishing makes this template selectable in every coordinator dashboard. It does not
            send or schedule any email, and it does not change a template a book has already copied.
          </Alert>

          {confirming && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>Publish to every coordinator dashboard?</AlertTitle>
              This becomes the live version of “{trimmedName}” immediately, in every book, with no
              release. The version it replaces is kept as history and nothing already copied
              changes.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {confirming ? (
          <>
            <Button onClick={() => setConfirming(false)} disabled={saving}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!canSave}
              onClick={publish}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? 'Publishing…' : 'Yes, publish'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={saveDraft} disabled={!canSave}>
              Save as draft
            </Button>
            <Button variant="contained" onClick={() => setConfirming(true)} disabled={!canSave}>
              Publish to all dashboards
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateDialog;
